import type {
  NetworkGraphEdgeModel,
  NetworkGraphNodeModel,
} from '../NetworkGraph';
import type { NetworkGraphPosition } from './computeRadialLayout';
import { nodeOuterRadius } from './nodeGeometry';

// gap between arrow and node
const EDGE_ENDPOINT_GAP = 4;
// Angular chord clearance between arrow tips that share a target — not the
// tip-to-node gap (that is EDGE_ENDPOINT_GAP).
const ARROWHEAD_MIN_SPACING = 4;
// Perpendicular separation for A→B / B→A pairs that would otherwise share one
// straight stroke (and stack arrowheads when one edge goes two-way on select).
const RECIPROCAL_LANE_OFFSET = 5;
const FULL_CIRCLE = Math.PI * 2;

interface EdgeEndpointOptions {
  source: NetworkGraphPosition;
  target: NetworkGraphPosition;
  sourceRadius: number;
  targetRadius: number;
  targetAngleOffset?: number;
  lateralOffset?: number;
}

export const edgeEndpoints = ({
  source,
  target,
  sourceRadius,
  targetRadius,
  targetAngleOffset = 0,
  lateralOffset = 0,
}: EdgeEndpointOptions) => {
  const deltaX = target.x - source.x;
  const deltaY = target.y - source.y;
  const distance = Math.hypot(deltaX, deltaY) || 1;
  const unitX = deltaX / distance;
  const unitY = deltaY / distance;
  const perpX = -unitY * lateralOffset;
  const perpY = unitX * lateralOffset;
  const targetCos = Math.cos(targetAngleOffset);
  const targetSin = Math.sin(targetAngleOffset);
  const targetUnitX = -unitX * targetCos + unitY * targetSin;
  const targetUnitY = -unitX * targetSin - unitY * targetCos;

  return {
    start: {
      x: source.x + unitX * (sourceRadius + EDGE_ENDPOINT_GAP) + perpX,
      y: source.y + unitY * (sourceRadius + EDGE_ENDPOINT_GAP) + perpY,
    },
    end: {
      x: target.x + targetUnitX * (targetRadius + EDGE_ENDPOINT_GAP) + perpX,
      y: target.y + targetUnitY * (targetRadius + EDGE_ENDPOINT_GAP) + perpY,
    },
  };
};

// A→B and B→A would otherwise occupy the same chord; nudge each into its own
// lane. Same signed offset for both — travel direction flips the perpendicular,
// so the pair separates without canceling.
export const reciprocalLaneOffsets = (
  edges: readonly Pick<NetworkGraphEdgeModel, 'id' | 'sourceId' | 'targetId'>[]
): Map<string, number> => {
  const byDirected = new Map<string, string>();
  for (const edge of edges) {
    byDirected.set(`${edge.sourceId}_${edge.targetId}`, edge.id);
  }

  const offsets = new Map<string, number>();
  for (const edge of edges) {
    const reverseId = byDirected.get(`${edge.targetId}_${edge.sourceId}`);
    if (!reverseId || offsets.has(edge.id)) {
      continue;
    }

    offsets.set(edge.id, RECIPROCAL_LANE_OFFSET);
    offsets.set(reverseId, RECIPROCAL_LANE_OFFSET);
  }

  return offsets;
};

// Labels sit on the edge midpoint so the rect masks the stroke under the text.
const EDGE_LABEL_FRACTION = 0.5;
const EDGE_LABEL_PADDING = 4;
const EDGE_LABEL_LINE_HEIGHT = 14;
const EDGE_LABEL_HEIGHT = EDGE_LABEL_LINE_HEIGHT + EDGE_LABEL_PADDING * 2;

const edgeLabelWidth = (label: string | undefined) =>
  Math.max(1, (label?.length ?? 0) * 6.4) + EDGE_LABEL_PADDING * 2;

export interface EdgeLabelBoxOptions {
  edge: NetworkGraphEdgeModel;
  source: NetworkGraphNodeModel;
  sourcePosition: NetworkGraphPosition;
  target: NetworkGraphNodeModel;
  targetPosition: NetworkGraphPosition;
  targetAngleOffset?: number;
  lateralOffset?: number;
}

export type EdgeGeometryOptions = Omit<EdgeLabelBoxOptions, 'edge'>;

interface TargetEndpointAngleOffsetsOptions {
  edges: readonly NetworkGraphEdgeModel[];
  positions: Readonly<Record<string, NetworkGraphPosition>>;
  nodeById: ReadonlyMap<string, NetworkGraphNodeModel>;
}

interface TargetEdgeAngle {
  id: string;
  angle: number;
}

const applyClusterOffsets = (
  cluster: readonly TargetEdgeAngle[],
  minimumAngle: number,
  offsets: Map<string, number>
) => {
  const first = cluster[0];
  const last = cluster.at(-1);
  if (cluster.length < 2 || !first || !last) {
    return;
  }

  const centerAngle = (first.angle + last.angle) / 2;
  const startAngle = centerAngle - ((cluster.length - 1) * minimumAngle) / 2;

  cluster.forEach((item, index) => {
    offsets.set(item.id, startAngle + index * minimumAngle - item.angle);
  });
};

const groupTargetEdgeAngles = ({
  edges,
  positions,
  nodeById,
}: TargetEndpointAngleOffsetsOptions) => {
  const edgesByTarget = new Map<string, TargetEdgeAngle[]>();

  for (const edge of edges) {
    const source = positions[edge.sourceId];
    const target = positions[edge.targetId];
    if (!source || !target || !nodeById.has(edge.targetId)) {
      continue;
    }

    const targetEdges = edgesByTarget.get(edge.targetId) ?? [];
    targetEdges.push({
      id: edge.id,
      angle:
        (Math.atan2(source.y - target.y, source.x - target.x) + FULL_CIRCLE) %
        FULL_CIRCLE,
    });
    edgesByTarget.set(edge.targetId, targetEdges);
  }

  return edgesByTarget;
};

const orderAroundLargestGap = (
  targetEdges: readonly TargetEdgeAngle[]
): TargetEdgeAngle[] => {
  const sorted = targetEdges.toSorted(
    (first, second) =>
      first.angle - second.angle || first.id.localeCompare(second.id)
  );
  let largestGapIndex = 0;
  let largestGap = -1;
  for (let index = 0; index < sorted.length; index += 1) {
    const nextAngle =
      sorted[(index + 1) % sorted.length].angle +
      (index === sorted.length - 1 ? FULL_CIRCLE : 0);
    const gap = nextAngle - sorted[index].angle;
    if (gap > largestGap) {
      largestGap = gap;
      largestGapIndex = index;
    }
  }

  return Array.from({ length: sorted.length }, (_, index) => {
    const sortedIndex = (largestGapIndex + 1 + index) % sorted.length;
    const item = sorted[sortedIndex];

    return {
      ...item,
      angle: item.angle + (sortedIndex <= largestGapIndex ? FULL_CIRCLE : 0),
    };
  });
};

const spreadTargetEdges = (
  targetEdges: readonly TargetEdgeAngle[],
  targetNode: NetworkGraphNodeModel,
  offsets: Map<string, number>
) => {
  const ordered = orderAroundLargestGap(targetEdges);
  const targetRadius = nodeOuterRadius(targetNode.size) + EDGE_ENDPOINT_GAP;
  const minimumAngle =
    2 * Math.asin(ARROWHEAD_MIN_SPACING / (2 * targetRadius));
  let cluster = [ordered[0]];

  for (const item of ordered.slice(1)) {
    const previous = cluster.at(-1);
    if (previous && item.angle - previous.angle < minimumAngle) {
      cluster.push(item);
    } else {
      applyClusterOffsets(cluster, minimumAngle, offsets);
      cluster = [item];
    }
  }
  applyClusterOffsets(cluster, minimumAngle, offsets);
};

export const targetEndpointAngleOffsets = (
  options: TargetEndpointAngleOffsetsOptions
) => {
  const edgesByTarget = groupTargetEdgeAngles(options);
  const offsets = new Map<string, number>();
  for (const [targetId, targetEdges] of edgesByTarget) {
    if (targetEdges.length < 2) {
      continue;
    }

    const targetNode = options.nodeById.get(targetId);
    if (!targetNode) {
      continue;
    }

    spreadTargetEdges(targetEdges, targetNode, offsets);
  }

  return offsets;
};

export const edgeGeometry = ({
  source,
  sourcePosition,
  target,
  targetPosition,
  targetAngleOffset,
  lateralOffset,
}: EdgeGeometryOptions) => {
  const { start, end } = edgeEndpoints({
    source: sourcePosition,
    target: targetPosition,
    sourceRadius: nodeOuterRadius(source.size),
    targetRadius: nodeOuterRadius(target.size),
    targetAngleOffset,
    lateralOffset,
  });

  return {
    start,
    end,
    length: Math.hypot(end.x - start.x, end.y - start.y),
  };
};

export const edgeLabelBox = ({
  edge,
  source,
  sourcePosition,
  target,
  targetPosition,
  targetAngleOffset,
  lateralOffset,
}: EdgeLabelBoxOptions) => {
  const { start, end } = edgeGeometry({
    source,
    sourcePosition,
    target,
    targetPosition,
    targetAngleOffset,
    lateralOffset,
  });
  const x = start.x + (end.x - start.x) * EDGE_LABEL_FRACTION;
  const y = start.y + (end.y - start.y) * EDGE_LABEL_FRACTION;
  // The label lies along its edge so dense arrow bundles read as a starburst
  // instead of a pile of horizontal boxes. Normalized to (-90°, 90°] so the
  // text never renders upside down.
  const rawAngle =
    (Math.atan2(end.y - start.y, end.x - start.x) * 180) / Math.PI;
  const angle = ((rawAngle + 450) % 180) - 90;
  const textWidth = edgeLabelWidth(edge.label);
  const textHeight = EDGE_LABEL_HEIGHT;
  // Collision solver works with axis-aligned boxes, so expose the rotated
  // label's bounding box there while the renderer keeps the raw text metrics.
  const radians = (angle * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));

  return {
    id: edge.id,
    x,
    y,
    width: cos * textWidth + sin * textHeight,
    height: sin * textWidth + cos * textHeight,
    angle,
    textWidth,
    textHeight,
    // Single candidate keeps the label pinned on the stroke; node labels
    // treat it as a fixed obstacle instead of shoving it off-edge.
    candidates: [{ x, y }],
  };
};
